import React from 'react';
import { useIntl } from 'react-intl';

import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';

import { i18n } from '../../widgetMacroI18n.ts';
import type { EmbedDescriptor } from '../../widgetMacroTypes.ts';

type Props = {
	descriptor: EmbedDescriptor;
};

/**
 * Renders the legacy Twitter widget as a plain anchor link.
 *
 * The original implementation injected a Twitter timeline/tweet widget script
 * (`platform.twitter.com/widgets.js`) client-side; that approach is no longer
 * safe in a CSP-constrained environment.
 *
 * Two sub-cases (§7 items 8–11):
 *  - **Timeline widget** (`widgetId` present) — shows a "Loading Twitter Widget"
 *    heading with the widget ID and a labelled link.
 *  - **Single tweet** (no `widgetId`) — falls back to a direct link to the tweet URL.
 */
export const TwitterWidgetEmbed = ({ descriptor }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	if (descriptor.widgetId) {
		return (
			<Box testId="widget-macro-twitter">
				<Text as="strong">{formatMessage(i18n.twitterWidgetHeading)}</Text>
				<Text as="p">
					{formatMessage(i18n.twitterWidgetMessage, { widgetId: descriptor.widgetId })}
				</Text>
				<Anchor
					href={descriptor.embedUrl}
					rel="noopener noreferrer"
					target="_blank"
					aria-label={formatMessage(i18n.twitterWidgetLink, { widgetId: descriptor.widgetId })}
				>
					{descriptor.embedUrl}
				</Anchor>
			</Box>
		);
	}

	return (
		<Box testId="widget-macro-twitter">
			<Anchor href={descriptor.embedUrl} rel="noopener noreferrer" target="_blank">
				{descriptor.embedUrl}
			</Anchor>
		</Box>
	);
};
